import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box,
  Typography,
  Paper,
  Tabs,
  Tab,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Avatar,
  Chip,
  Alert,
  Card,
  CardContent,
  Divider,
  Button,
  Switch,
  FormControlLabel,
  Pagination,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  IconButton,
  Tooltip,
  CircularProgress,
  Badge,
} from '@mui/material';
import {
  Message as MessageIcon,
  WhatsApp as WhatsAppIcon,
  Group as GroupIcon,
  Clear as ClearIcon,
  Delete as DeleteIcon,
  DirectionsCar as RideIcon,
} from '@mui/icons-material';
import { useSocket } from '../contexts/SocketContext';
import { whatsappAccountService, WhatsAppAccount } from '../services/whatsappAccountService';
import { liveMessageService, ArchivedMessage } from '../services/liveMessageService';
import toast from 'react-hot-toast';

const PAGE_SIZE = 20;

interface LiveMessage {
  _id: string;
  messageId: string;
  sessionId: string;
  groupName: string;
  sender: string;
  content: string;
  timestamp: Date;
  isRideRelated: boolean;
  accountName: string;
}

const fromArchive = (m: ArchivedMessage): LiveMessage => ({
  _id: m._id,
  messageId: m.messageId,
  sessionId: m.sessionId,
  groupName: m.groupName || 'Unknown',
  sender: m.sender || '',
  content: m.body,
  timestamp: new Date(m.receivedAt),
  isRideRelated: m.isRideRelated,
  accountName: m.accountName || 'Unknown',
});

interface TabPanelProps { children?: React.ReactNode; index: number; value: number }
function TabPanel({ children, value, index, ...other }: TabPanelProps) {
  return (
    <div role="tabpanel" hidden={value !== index} {...other}>
      {value === index && <Box sx={{ p: { xs: 1, sm: 3 } }}>{children}</Box>}
    </div>
  );
}

interface ConfirmDialogProps {
  open: boolean;
  title: string;
  message: string;
  onConfirm: () => void;
  onCancel: () => void;
  loading?: boolean;
}
function ConfirmDialog({ open, title, message, onConfirm, onCancel, loading }: ConfirmDialogProps) {
  return (
    <Dialog open={open} onClose={onCancel} maxWidth="xs" fullWidth>
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        <DialogContentText>{message}</DialogContentText>
      </DialogContent>
      <DialogActions>
        <Button onClick={onCancel} disabled={loading}>Cancel</Button>
        <Button onClick={onConfirm} color="error" variant="contained" disabled={loading}>
          {loading ? 'Deleting...' : 'Delete'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

const LiveMessages: React.FC = () => {
  const [accounts, setAccounts] = useState<WhatsAppAccount[]>([]);
  const [messages, setMessages] = useState<LiveMessage[]>([]);
  const [activeTab, setActiveTab] = useState(0);
  const [isListening, setIsListening] = useState(true);
  const [loading, setLoading] = useState(true);
  const [pageLoading, setPageLoading] = useState(false);

  // page and total per tab: index 0 = All, 1..N = per account
  const [pages, setPages] = useState<number[]>([1]);
  const [totals, setTotals] = useState<number[]>([0]);

  // Refs to avoid stale closures in socket handler
  const activeTabRef = useRef(0);
  const accountsRef = useRef<WhatsAppAccount[]>([]);
  const pagesRef = useRef<number[]>([1]);

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmTitle, setConfirmTitle] = useState('');
  const [confirmMessage, setConfirmMessage] = useState('');
  const [confirmAction, setConfirmAction] = useState<() => Promise<void>>(() => async () => {});
  const [confirmLoading, setConfirmLoading] = useState(false);

  const { socket } = useSocket();

  // ── Core fetch ─────────────────────────────────────────────────────────────
  const loadPage = useCallback(async (tabIndex: number, page: number, currentAccounts: WhatsAppAccount[]) => {
    setPageLoading(true);
    try {
      const sessionId = tabIndex === 0 ? undefined : currentAccounts[tabIndex - 1]?.sessionId;
      const response = await liveMessageService.fetchMessages({ page, limit: PAGE_SIZE, sessionId });
      setMessages(response.messages.map(fromArchive));
      setTotals(prev => {
        const next = [...prev];
        next[tabIndex] = response.pagination.total;
        return next;
      });
    } catch {
      toast.error('Failed to load messages');
    } finally {
      setPageLoading(false);
    }
  }, []);

  // ── Initial accounts load ───────────────────────────────────────────────
  useEffect(() => {
    (async () => {
      try {
        setLoading(true);
        const accs = await whatsappAccountService.getAccounts();
        setAccounts(accs);
        accountsRef.current = accs;
        const tabCount = accs.length + 1;
        setPages(Array(tabCount).fill(1));
        pagesRef.current = Array(tabCount).fill(1);
        setTotals(Array(tabCount).fill(0));
        await loadPage(0, 1, accs);
      } catch {
        toast.error('Failed to load WhatsApp accounts');
      } finally {
        setLoading(false);
      }
    })();
  }, [loadPage]);

  // ── Tab switch ─────────────────────────────────────────────────────────────
  const handleTabChange = (_: React.SyntheticEvent, newTab: number) => {
    setActiveTab(newTab);
    activeTabRef.current = newTab;
    // Reset to page 1 for the new tab and fetch
    setPages(prev => {
      const next = [...prev];
      next[newTab] = 1;
      pagesRef.current = next;
      return next;
    });
    loadPage(newTab, 1, accountsRef.current);
  };

  // ── Page navigation ────────────────────────────────────────────────────────
  const handlePageChange = (_: React.ChangeEvent<unknown>, p: number) => {
    setPages(prev => {
      const next = [...prev];
      next[activeTabRef.current] = p;
      pagesRef.current = next;
      return next;
    });
    loadPage(activeTabRef.current, p, accountsRef.current);
  };

  // ── Socket listener — refresh page 1 of current tab on new message ────────
  useEffect(() => {
    if (!socket || !isListening) return;

    const handleNewMessage = (data: any) => {
      const tab = activeTabRef.current;
      const currentPage = pagesRef.current[tab] ?? 1;
      if (currentPage === 1) {
        loadPage(tab, 1, accountsRef.current);
      }
      if (data.potentialRideRequest || data.isRideRelated) {
        toast.success(`Ride request in ${data.groupName || 'a group'}`);
      }
    };

    socket.on('whatsapp:message', handleNewMessage);
    socket.on('whatsapp:liveMessage', handleNewMessage);
    return () => {
      socket.off('whatsapp:message', handleNewMessage);
      socket.off('whatsapp:liveMessage', handleNewMessage);
    };
  }, [socket, isListening, loadPage]);

  // ── Confirm helper ─────────────────────────────────────────────────────────
  const askConfirm = (title: string, msg: string, action: () => Promise<void>) => {
    setConfirmTitle(title);
    setConfirmMessage(msg);
    setConfirmAction(() => action);
    setConfirmOpen(true);
  };

  const runConfirm = async () => {
    setConfirmLoading(true);
    try { await confirmAction(); } finally {
      setConfirmLoading(false);
      setConfirmOpen(false);
    }
  };

  // ── Delete single ──────────────────────────────────────────────────────────
  const handleDeleteMessage = (msg: LiveMessage) => {
    askConfirm(
      'Delete message',
      'Remove this message from the database permanently?',
      async () => {
        await liveMessageService.deleteMessage(msg._id);
        // Reload current page
        await loadPage(activeTabRef.current, pagesRef.current[activeTabRef.current] ?? 1, accountsRef.current);
        toast.success('Message deleted');
      }
    );
  };

  // ── Clear account ──────────────────────────────────────────────────────────
  const handleClearAccount = (sessionId: string, accountName: string, tabIndex: number) => {
    askConfirm(
      `Clear ${accountName}`,
      `Delete all stored messages for "${accountName}" from the database? This cannot be undone.`,
      async () => {
        await liveMessageService.clearMessages(sessionId);
        setPages(prev => { const next = [...prev]; next[tabIndex] = 1; pagesRef.current = next; return next; });
        await loadPage(tabIndex, 1, accountsRef.current);
        toast.success(`Messages cleared for ${accountName}`);
      }
    );
  };

  // ── Clear all ──────────────────────────────────────────────────────────────
  const handleClearAll = () => {
    askConfirm(
      'Clear all messages',
      'Delete every stored WhatsApp message from the database? This cannot be undone.',
      async () => {
        await liveMessageService.clearMessages();
        const tabCount = accountsRef.current.length + 1;
        setPages(Array(tabCount).fill(1));
        pagesRef.current = Array(tabCount).fill(1);
        setTotals(Array(tabCount).fill(0));
        setMessages([]);
        toast.success('All messages cleared');
      }
    );
  };

  // ── Helpers ────────────────────────────────────────────────────────────────
  const formatTimestamp = (ts: Date) =>
    new Intl.DateTimeFormat('en-US', { hour: '2-digit', minute: '2-digit', second: '2-digit' }).format(ts);

  // ── Sub-components ─────────────────────────────────────────────────────────
  const MessageRow = ({ message }: { message: LiveMessage }) => (
    <ListItem alignItems="flex-start" secondaryAction={
      <Tooltip title="Delete message">
        <IconButton size="small" color="error" onClick={() => handleDeleteMessage(message)}>
          <DeleteIcon fontSize="small" />
        </IconButton>
      </Tooltip>
    }>
      <ListItemAvatar>
        <Avatar sx={{ bgcolor: message.isRideRelated ? 'success.main' : 'primary.main' }}>
          {message.isRideRelated ? <RideIcon /> : <GroupIcon />}
        </Avatar>
      </ListItemAvatar>
      <ListItemText
        primary={
          <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
            <Typography variant="subtitle2" component="span">{message.groupName}</Typography>
            {message.accountName && (
              <Chip size="small" label={message.accountName} variant="outlined" />
            )}
            {message.isRideRelated && (
              <Chip size="small" icon={<RideIcon sx={{ fontSize: 12 }} />} label="Ride" color="warning" />
            )}
            <Typography variant="caption" color="textSecondary">
              {formatTimestamp(message.timestamp)}
            </Typography>
          </Box>
        }
        secondary={
          <Box>
            <Typography variant="body2" color="textSecondary" gutterBottom>
              From: {message.sender}
            </Typography>
            <Typography variant="body2">{message.content}</Typography>
          </Box>
        }
      />
    </ListItem>
  );

  const MessageList = ({ tabIndex }: { tabIndex: number }) => {
    const total = totals[tabIndex] ?? 0;
    const currentPage = pages[tabIndex] ?? 1;
    const pageCount = Math.ceil(total / PAGE_SIZE) || 1;

    return (
      <>
        {pageLoading && (
          <Box display="flex" justifyContent="center" py={2}>
            <CircularProgress size={24} />
          </Box>
        )}
        <List>
          {messages.map((msg, idx) => (
            <React.Fragment key={msg._id}>
              <MessageRow message={msg} />
              {idx < messages.length - 1 && <Divider variant="inset" component="li" />}
            </React.Fragment>
          ))}
          {messages.length === 0 && !pageLoading && (
            <ListItem>
              <ListItemText
                primary={
                  <Typography variant="body1" color="textSecondary" align="center">
                    {isListening ? 'No messages yet. Waiting for live messages...' : 'Message listening is paused'}
                  </Typography>
                }
              />
            </ListItem>
          )}
        </List>
        {pageCount > 1 && (
          <Box display="flex" justifyContent="center" mt={2}>
            <Pagination
              count={pageCount}
              page={currentPage}
              onChange={handlePageChange}
              color="primary"
              size="small"
            />
          </Box>
        )}
      </>
    );
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3} flexWrap="wrap" gap={2}>
        <Typography variant="h4" component="h1">Live WhatsApp Messages</Typography>
        <Box display="flex" alignItems="center" gap={2} flexWrap="wrap">
          <FormControlLabel
            control={<Switch checked={isListening} onChange={(e) => setIsListening(e.target.checked)} color="primary" />}
            label={isListening ? 'Listening' : 'Paused'}
          />
          <Button
            variant="outlined"
            color="error"
            startIcon={<ClearIcon />}
            onClick={handleClearAll}
            disabled={totals[0] === 0}
          >
            Clear All
          </Button>
        </Box>
      </Box>

      {accounts.length === 0 ? (
        <Alert severity="info">No WhatsApp accounts found. Please add and connect WhatsApp accounts first.</Alert>
      ) : (
        <>
          <Paper sx={{ mb: 3 }}>
            <Tabs value={activeTab} onChange={handleTabChange} variant="scrollable" scrollButtons="auto">
              <Tab
                label={
                  <Box display="flex" alignItems="center" gap={1}>
                    <MessageIcon />
                    <span>All Messages</span>
                    <Badge badgeContent={totals[0] || 0} color="primary" max={9999} />
                  </Box>
                }
              />
              {accounts.map((account, idx) => (
                <Tab
                  key={account.sessionId}
                  label={
                    <Box display="flex" alignItems="center" gap={1}>
                      <Avatar sx={{ width: 20, height: 20 }}>
                        <WhatsAppIcon sx={{ fontSize: 12 }} />
                      </Avatar>
                      <span>{account.accountName}</span>
                      <Badge badgeContent={totals[idx + 1] || 0} color="primary" max={9999} />
                      <Chip size="small" label={account.isReady ? 'Ready' : 'Not Ready'} color={account.isReady ? 'success' : 'warning'} variant="outlined" />
                    </Box>
                  }
                />
              ))}
            </Tabs>
          </Paper>

          {/* All Messages tab */}
          <TabPanel value={activeTab} index={0}>
            <Card>
              <CardContent>
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={2} flexWrap="wrap" gap={1}>
                  <Typography variant="h6">
                    All Messages
                    {totals[0] > 0 && <Typography component="span" variant="body2" color="text.secondary" ml={1}>({totals[0]} total)</Typography>}
                  </Typography>
                  <Button size="small" variant="outlined" color="error" startIcon={<ClearIcon />} onClick={handleClearAll} disabled={totals[0] === 0}>
                    Clear All
                  </Button>
                </Box>
                <MessageList tabIndex={0} />
              </CardContent>
            </Card>
          </TabPanel>

          {/* Per-account tabs */}
          {accounts.map((account, index) => (
            <TabPanel key={account.sessionId} value={activeTab} index={index + 1}>
              <Card>
                <CardContent>
                  <Box display="flex" justifyContent="space-between" alignItems="center" mb={2} flexWrap="wrap" gap={1}>
                    <Typography variant="h6">
                      {account.accountName}
                      {totals[index + 1] > 0 && <Typography component="span" variant="body2" color="text.secondary" ml={1}>({totals[index + 1]} total)</Typography>}
                    </Typography>
                    <Box display="flex" gap={1} alignItems="center" flexWrap="wrap">
                      <Chip size="small" label={account.isReady ? 'Ready & Active' : 'Not Ready'} color={account.isReady ? 'success' : 'warning'} />
                      <Button
                        size="small"
                        variant="outlined"
                        color="error"
                        startIcon={<ClearIcon />}
                        onClick={() => handleClearAccount(account.sessionId, account.accountName, index + 1)}
                        disabled={totals[index + 1] === 0}
                      >
                        Clear
                      </Button>
                    </Box>
                  </Box>
                  {!account.isReady && (
                    <Alert severity="warning" sx={{ mb: 2 }}>
                      This account is not ready. Connect and authenticate it to receive live messages.
                    </Alert>
                  )}
                  <MessageList tabIndex={index + 1} />
                </CardContent>
              </Card>
            </TabPanel>
          ))}
        </>
      )}

      <ConfirmDialog
        open={confirmOpen}
        title={confirmTitle}
        message={confirmMessage}
        onConfirm={runConfirm}
        onCancel={() => setConfirmOpen(false)}
        loading={confirmLoading}
      />
    </Box>
  );
};

export default LiveMessages;
